
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Users, Crown, Target, Lightbulb, TrendingUp, Star } from 'lucide-react';
import { EnhancedTeamAnalysis } from '@/types/teamAnalysisTypes';
import InlineBehavioralChart from '@/components/recruitment/InlineBehavioralChart';

interface EnhancedTeamAnalysisDashboardProps {
  analysis: EnhancedTeamAnalysis;
  onBack: () => void;
}

const EnhancedTeamAnalysisDashboard: React.FC<EnhancedTeamAnalysisDashboardProps> = ({
  analysis,
  onBack
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-status-success bg-status-success/10 border-status-success/30';
    if (score >= 60) return 'text-status-warning bg-status-warning/10 border-status-warning/30';
    return 'text-status-error bg-status-error/10 border-status-error/30';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    return 'Needs Attention';
  };

  const manager = analysis.members.find(m => m.id === analysis.context.managerId);

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center mb-6">
          <Button
            variant="ghost"
            onClick={onBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Selection
          </Button>
        </div>

        {/* Header */}
        <Card className="border border-border mb-6">
          <CardHeader className="text-center">
            <CardTitle className="flex items-center justify-center gap-3 text-[20px] font-semibold text-foreground">
              <Users className="h-6 w-6 text-primary" />
              {analysis.context.teamType} Team Analysis
            </CardTitle>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Badge variant="outline" className="bg-muted text-muted-foreground border-border px-4 py-2">
                {analysis.context.companyPhase} Phase
              </Badge>
              <Badge
                variant="secondary"
                className={`px-6 py-3 text-[16px] font-semibold ${getScoreColor(analysis.overallTeamScore)}`}
              >
                {analysis.overallTeamScore}% - {getScoreLabel(analysis.overallTeamScore)}
              </Badge>
              {manager && (
                <Badge variant="outline" className="bg-muted text-muted-foreground border-border px-4 py-2">
                  <Crown className="h-4 w-4 mr-1" />
                  {manager.name}
                </Badge>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Manager Analysis */}
        {manager && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-status-success">
                  <Star className="h-5 w-5" />
                  Manager Superpowers
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.managerAnalysis.superpowers.map((superpower, index) => (
                  <div key={index} className="bg-status-success/10 p-3 rounded-lg">
                    <p className="text-[13px] text-foreground font-medium">{superpower}</p>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="border border-border">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-status-warning">
                  <Target className="h-5 w-5" />
                  Areas of Attention
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.managerAnalysis.attentionAreas.map((area, index) => (
                  <div key={index} className="bg-status-warning/10 p-3 rounded-lg">
                    <p className="text-[13px] text-foreground font-medium">{area}</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Key Insights & Recommendations */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-primary">
                <TrendingUp className="h-5 w-5" />
                Key Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.keyInsights.map((insight, index) => (
                <div key={index} className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-[13px] text-foreground">{insight}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="border border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-secondary-foreground">
                <Lightbulb className="h-5 w-5" />
                Recommendations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {analysis.recommendations.map((recommendation, index) => (
                <div key={index} className="bg-muted/50 p-3 rounded-lg">
                  <p className="text-[13px] text-foreground">{recommendation}</p>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Team Members with Optimized Roles */}
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-primary" />
              Team Members & Optimized Roles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
              {analysis.members.map(member => {
                // Prepare scores for InlineBehavioralChart
                const chartScores = {
                  dominancePercentile: member.scores.dominance,
                  extraversionPercentile: member.scores.extraversion,
                  patiencePercentile: member.scores.patience,
                  formalityPercentile: member.scores.formality,
                  objectivityPercentile: member.scores.objectivity,
                };

                return (
                  <div
                    key={member.id}
                    className={`p-4 rounded-lg border-2 ${
                      member.id === analysis.context.managerId
                        ? 'border-muted bg-muted/30'
                        : 'border-border bg-card'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <h4 className="font-semibold text-foreground">{member.name}</h4>
                      {member.id === analysis.context.managerId && (
                        <Crown className="h-4 w-4 text-primary" />
                      )}
                    </div>

                    <p className="text-[11px] text-muted-foreground mb-3">{member.email}</p>

                    <Badge variant="outline" className="w-full justify-center mb-3 bg-muted text-muted-foreground border-border">
                      {analysis.individualRoles[member.id]}
                    </Badge>

                    {/* Behavioral Chart */}
                    <div className="mb-3">
                      <InlineBehavioralChart
                        scores={chartScores}
                        size="card"
                        assessmentVariant="model3"
                      />
                    </div>

                    <div className="space-y-2">
                      {Object.entries(member.scores).slice(0, 4).map(([trait, score]) => (
                        <div key={trait}>
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-[11px] font-medium capitalize text-foreground">{trait.charAt(0)}</span>
                            <span className="text-[11px] text-muted-foreground">{score}%</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-1.5">
                            <div
                              className="bg-primary h-1.5 rounded-full"
                              style={{ width: `${score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EnhancedTeamAnalysisDashboard;
